import { useEffect, useId, useRef, useState } from 'react'

import { cn } from '#app/utils/misc.js'
import { Button } from './button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from './command'
import { Icon } from './icon'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export interface Option {
	label: string
	value: string
}
export type Options = Option[]

export type MySelectProps = {
	defaultOptions?: Options
	isMulti: boolean
	isSearchable?: boolean
	name: string
	options: Options
	placeholder: string
	singularItem?: string
	className?: string
	id?: string
	pluralItem?: string
	onChange?: (selected: Options) => unknown
}

export default function MySelect({
	defaultOptions = [],
	isMulti = true,
	isSearchable = true,
	name,
	options,
	placeholder,
	onChange,
	id = '',
	className = '',
	singularItem,
	pluralItem,
}: MySelectProps) {
	const isMounted = useRef<boolean>(false)
	const display = (
		count: number,
		values: Options,
		{ isMulti, isSingle }: { isMulti: boolean; isSingle: boolean },
	): React.ReactNode | string => {
		if (count === 0) {
			return (
				<span className="text-muted-foreground">
					{placeholder || 'Select option'}
				</span>
			)
		}
		if (isSingle) {
			return values[0].label
		}
		if (count === 1) {
			return `1 ${singularItem ?? 'option'} selected`
		}
		if (count >= 1 || isMulti) {
			return `${count} ${pluralItem ?? `${singularItem}s` ?? 'options'} selected`
		}
		return 'Unknown'
	}
	const key = useId()
	const [open, setOpen] = useState<boolean>(false)
	const [selected, setSelected] = useState<Options>(defaultOptions)
	const selectedValues = selected.map(opt => opt.value)
	const isSingle = !isMulti
	const textToDisplay = display(selectedValues.length, selected, {
		isMulti,
		isSingle,
	})
	useEffect(() => {
		if (isMounted.current) {
			if (onChange) onChange(selected)
		} else {
			isMounted.current = true
		}
		// eslint-disable-next-line
	}, [selected])
	return (
		<div className="w-full">
			{selectedValues.map(value => (
				<input
					key={`select-dropdown-${key}-value=${value}`}
					type="text"
					name={name}
					hidden
					readOnly
					value={value}
				/>
			))}
			<Popover open={open} onOpenChange={popOverOpen => setOpen(popOverOpen)}>
				<PopoverTrigger asChild>
					<Button
						id={id}
						variant="outline"
						role="combobox"
						className={`flex w-full justify-between px-3 font-normal ${className}`}
					>
						{textToDisplay}
						<Icon name="chevrons-up-down" className="h-4 w-4" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="h-auto max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] overflow-y-auto p-0">
					<Command>
						{isSearchable ? (
							<>
								<CommandInput placeholder="Search" />
							</>
						) : null}
						<CommandEmpty>Nothing found.</CommandEmpty>
						<CommandGroup className="">
							{options.map(option => {
								const isSelected = selectedValues.includes(option.value)
								return (
									<CommandItem
										value={option.label}
										key={option.value}
										tabIndex={0}
										className={`relative w-full cursor-pointer pr-6 ${isSelected && 'bg-accent'}`}
										onSelect={() => {
											if (isSingle) {
												setOpen(false)
												return setSelected([option])
											}
											if (selectedValues.includes(option.value)) {
												const filtered = selected.filter(
													s => s.value !== option.value,
												)
												return setSelected(filtered)
											}
											setSelected([...selected, option])
										}}
									>
										{option.label}
										<Icon
											name="check"
											className={cn(
												'absolute right-1 h-4 w-4',
												isSelected ? 'opacity-100' : 'opacity-0',
											)}
										/>
									</CommandItem>
								)
							})}
						</CommandGroup>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	)
}
